import { cn } from '@/lib/utils'
import ResponsiveImage from '@/v2/components/helpers/responsive-image'
import SectionHead from '@/v2/components/sections/landing-pages/section-head'
import SectionWrapper from '@/v2/components/wrappers/section-wrapper'
import { CardType } from '@/v2/types/cards'
import { domAnimation, LazyMotion, m } from 'framer-motion'
import Link from 'next/link'
import React from 'react'

interface BenefitsSectionSectionProps {
  title: string
  description?: string
  cards: CardType[]
  className?: string
  isBgDark?: boolean
  sectionTitleClassName?: string
}

const BenefitsSection: React.FC<BenefitsSectionSectionProps> = props => {
  const { title, description, cards, className, isBgDark = false, sectionTitleClassName } = props

  const cardBackground = isBgDark ? 'bg-white' : 'bg-[#F9F8F8]'

  return (
    <LazyMotion features={domAnimation}>
      <m.section
        viewport={{ once: false, amount: 0.2 }}
        className={cn('landing-pages-wr section-spacing', isBgDark ? 'bg-[#F9F8F8]' : '', className)}
      >
        <SectionWrapper className='xl:max-w-[1350px]'>
          <SectionHead title={title} description={description} className={sectionTitleClassName} />

          <div className='grid md-mn:grid-cols-2 grid-cols-1 md-mn:gap-3 lg-mn:gap-[34px] xl-min:gap-12 gap-6'>
            {cards.map(card => {
              return (
                <m.article
                  key={card.id}
                  whileInView='visible'
                  initial='hidden'
                  viewport={{ once: false, amount: 0.2 }}
                  className={cn(
                    `flex items-center lg-mn:flex-row flex-col lg-mn:p-5 md-mn:p-3 p-4 gap-4 lg-mn:gap-6 border-2
                  border-[#E5E5E5] transition-all rounded-md hover:border-[#0A9CFF] lg-mn:text-left text-center`,
                    cardBackground
                  )}
                >
                  {card.image && (
                    <div className='lg-mn:self-start self-center'>
                      <ResponsiveImage
                        src={card.image.src}
                        alt={card.image.alt}
                        className={cn('h-[130px] w-[130px]')}
                      />
                    </div>
                  )}
                  <div className='[&>h3]:mb-[11px] xl-min:[&>h3]:text-xl w-full [&>h3]:text-lg [&>h3]:font-semibold [&>h3]:text-paragraph [&>h3]:leading-8'>
                    <h3>{card.title}</h3>
                    <p className='text-paragraph leading-6 font-normal text-base'>{card.description}</p>
                    {card.link && (
                      <Link
                        href={card.link.href}
                        className='text-[#0A9CFF] font-bold mt-3 inline-flex items-center gap-1 group'
                      >
                        {card.link.text}
                        <svg
                          xmlns='http://www.w3.org/2000/svg'
                          fill='none'
                          viewBox='0 0 24 24'
                          strokeWidth={2}
                          stroke='currentColor'
                          className='w-4 h-4 transition-transform duration-200 group-hover:translate-x-1'
                        >
                          <path strokeLinecap='round' strokeLinejoin='round' d='M5 12h14M12 5l7 7-7 7' />
                        </svg>
                      </Link>
                    )}
                  </div>
                </m.article>
              )
            })}
          </div>
        </SectionWrapper>
      </m.section>
    </LazyMotion>
  )
}

export default BenefitsSection
